// Consent, and what follows from it: remembering the ad that brought someone
// here, and putting a reference to that on the Stripe link they buy through.
//
// The reference is a random `attr_` string and nothing else. It means nothing
// without the licence server's record, which is the only place the campaign
// and click identifiers are kept, and which is deleted after 90 days.
//
// Every path here ends with a working checkout. A storage denial, a licence
// server that is down, a request that times out: each of them loses the
// attribution and none of them loses the sale.

import { captureAttribution, paymentLink } from "./attribution.js"
import { clearGoogleAnalytics, loadGoogleAnalytics } from "./google-analytics.js"

// v2 also covers Google Analytics. A v1 answer was about ads alone, so it no
// longer counts and the visitor is asked again.
const CONSENT = "inboxctl.ad_measurement_consent_v2"
const OLD_CONSENT = "inboxctl.ad_measurement_consent"
const ATTRIBUTION = "inboxctl.ad_attribution"
const REFERENCES = "inboxctl.ad_references"

const DAY = 86400000
// How long the answer is remembered. Long enough not to be nagging, short
// enough to be a decision somebody made this year.
const CONSENT_DAYS = 180
// How long a reference stays withdrawable in this browser. The server deletes
// its own copy on the same schedule.
const REFERENCE_DAYS = 90
// A licence server that has not answered in this long has lost its chance;
// the buyer is going to Stripe either way.
const TIMEOUT = 3000

const VALID_REFERENCE = /^attr_[a-f0-9]{32}$/

export const readStored = (storage, key) => {
  try {
    return JSON.parse(storage.getItem(key) ?? "null")
  } catch {
    // A private window, blocked site data, or a value from an older shape of
    // this code. All three mean the same thing: nothing is remembered.
    return null
  }
}

export const writeStored = (storage, key, value) => {
  try {
    if (value === null) storage.removeItem(key)
    else storage.setItem(key, JSON.stringify(value))
    return true
  } catch {
    return false
  }
}

// The SHA-256 of what an attribution record says, so that a second load of the
// same campaign URL reuses the reference the first one registered instead of
// filling the server with duplicates of one visitor.
//
// The clicks and the campaigns, and not the whole record: `expires_at` moves
// every time the campaign is seen again, and hashing it would make every reload
// a new record.
const digest = async (attribution) => {
  const subject = JSON.stringify({
    clicks: attribution.clicks,
    utm: attribution.utm,
    first_utm: attribution.first_utm,
  })
  const bytes = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(subject))
  return Array.from(new Uint8Array(bytes), (byte) => byte.toString(16).padStart(2, "0")).join("")
}

// `document`, `location`, `storage` and `fetch` are arguments and not
// globals so the whole of this can be exercised without a browser.
//
// `onConsent(granted)` runs for a stored grant on load and for every choice
// made in the panel; it switches Google Analytics on and off.
export const installSalesTracking = ({ document, location, storage, fetchRequest = fetch, onConsent = () => {} }) => {
  const panel = document.getElementById("ad-measurement")
  if (!panel) return

  // Which licence server this build talks to is decided at build time, by
  // `DeployTarget`: the sandbox site registers against the sandbox worker,
  // and the production site against the production one. There is no query
  // parameter that moves a visitor between them.
  const api = panel.dataset.endpoint
  if (!api) return

  // Every plan's Stripe link. Their `href` as rendered is the checkout without
  // any of this, and is what they are put back to whenever consent is absent.
  const links = [...document.querySelectorAll("[data-checkout]")].map((link) => ({
    link,
    base: link.href,
  }))

  writeStored(storage, OLD_CONSENT, null)
  let consent = readStored(storage, CONSENT)
  if (!(consent?.expires_at > Date.now())) consent = null
  if (consent?.granted) onConsent(true)
  let attribution = null
  // Bumped on every refresh and every choice. An answer that arrives from a
  // request started before the visitor changed their mind belongs to nobody,
  // and this is how it is recognised and dropped.
  let generation = 0
  let pending = Promise.resolve()

  const setLinks = (reference) => {
    for (const { link, base } of links) {
      link.href = attribution ? paymentLink(base, attribution, reference) : base
    }
  }

  const storedReferences = () => {
    const value = readStored(storage, REFERENCES)
    return Array.isArray(value)
      ? value.filter((item) => item && VALID_REFERENCE.test(item.reference ?? "") && item.api === api)
      : []
  }

  const refresh = async () => {
    const current = ++generation
    attribution = null
    setLinks()
    if (!consent?.granted) return

    attribution = captureAttribution(readStored(storage, ATTRIBUTION), location.search)
    writeStored(storage, ATTRIBUTION, attribution)
    if (!attribution) return

    const snapshot = await digest(attribution)
    if (current !== generation || !consent?.granted) return

    const references = storedReferences().filter((item) => item.expires_at > Date.now())
    const known = references.find((item) => item.snapshot === snapshot)
    if (known) {
      setLinks(known.reference)
      return
    }

    // The campaign goes on the link before the server has answered. If the
    // answer never comes, the sale is still tagged with the campaign in
    // Stripe's own record, only the platform-side conversion is lost.
    setLinks()
    try {
      const response = await fetchRequest(api, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        signal: AbortSignal.timeout(TIMEOUT),
        body: JSON.stringify({ consent: true, attribution }),
      })
      if (!response.ok) throw new Error("attribution was not saved")
      const { reference } = await response.json()
      if (!VALID_REFERENCE.test(reference ?? "")) throw new Error("invalid attribution reference")

      references.push({ reference, api, snapshot, expires_at: Date.now() + REFERENCE_DAYS * DAY })
      writeStored(storage, REFERENCES, references)

      // Consent was withdrawn while this was in flight. The record exists, so
      // it is deleted and not merely forgotten.
      if (current !== generation || !consent?.granted) {
        await fetchRequest(`${api}/${reference}`, { method: "DELETE", credentials: "omit" })
        return
      }
      setLinks(reference)
    } catch {
      console.warn("purchase attribution is unavailable")
    }
  }

  const update = () => {
    pending = refresh()
    return pending
  }

  // A visitor who presses Subscribe before the registration has finished still
  // gets the reference. Only a plain left click is held: a middle click or a
  // modified one opens a tab, and preventing that would be worse than losing
  // the attribution.
  for (const { link } of links) {
    link.addEventListener("click", async (event) => {
      if (!consent?.granted) return
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      event.preventDefault()
      await pending
      location.assign(link.href)
    })
  }

  const choose = async (granted) => {
    consent = { granted, expires_at: Date.now() + CONSENT_DAYS * DAY }
    writeStored(storage, CONSENT, consent)
    panel.hidden = true
    onConsent(granted)

    if (!granted) {
      ++generation
      attribution = null
      setLinks()
      writeStored(storage, ATTRIBUTION, null)

      // Withdrawal is more than forgetting. Every reference this browser
      // registered is revoked on the server, which cancels any purchase event
      // not yet accepted by an ad platform. A reference whose deletion fails
      // is kept so the next withdrawal tries it again.
      const remaining = []
      await Promise.all(storedReferences().map(async (item) => {
        try {
          const response = await fetchRequest(`${item.api}/${item.reference}`, {
            method: "DELETE",
            credentials: "omit",
            signal: AbortSignal.timeout(TIMEOUT),
          })
          if (!response.ok) remaining.push(item)
        } catch {
          remaining.push(item)
        }
      }))
      writeStored(storage, REFERENCES, remaining.length ? remaining : null)
    }

    await update()
  }

  panel.querySelector("[data-accept]").addEventListener("click", () => choose(true))
  panel.querySelector("[data-reject]").addEventListener("click", () => choose(false))
  document.getElementById("ad-measurement-settings")?.addEventListener("click", () => {
    panel.hidden = false
    // The way out is the focused control. Nobody should have to tab past
    // "Allow" to decline.
    panel.querySelector("[data-reject]").focus()
  })

  // Asked once. An answer already given is not asked again until it lapses.
  panel.hidden = consent !== null
  return update()
}

if (typeof document !== "undefined") {
  document.addEventListener("DOMContentLoaded", () => {
    let storage
    try {
      storage = window.localStorage
    } catch {
      // Blocked site data: the choice then applies to this page and no other.
      storage = { getItem: () => null, setItem: () => {}, removeItem: () => {} }
    }
    const panel = document.getElementById("ad-measurement")?.dataset ?? {}
    const ids = [panel.analyticsId, panel.adsId]
    const onConsent = (granted) =>
      granted
        ? loadGoogleAnalytics({ document, window, ids })
        : clearGoogleAnalytics({ document, window, ids, hostname: window.location.hostname })
    installSalesTracking({ document, location: window.location, storage, onConsent })
  })
}
